import { Card, Skeleton } from '@/components/ui';
import { PortfolioPnlChart } from './PortfolioPnlChart';
import type { PlaeePnlDataPointDto } from '@/lib/models';
import { cn } from '@/lib/utils';

/**
 * Selectable timeframe shown in the bottom pill toggle. The PnL series is
 * daily-grained (no intraday points), so there is deliberately no `1D`.
 */
export type PortfolioTimeframe = '1W' | '1M' | 'All';

const TIMEFRAMES: PortfolioTimeframe[] = ['1W', '1M', 'All'];

export interface PortfolioChartProps {
  /** Realized-PnL points for the active timeframe (`GET /plaee/positions/pnl`). */
  points: PlaeePnlDataPointDto[];
  /** Active timeframe (controlled — switching refetches the series). */
  timeframe: PortfolioTimeframe;
  onTimeframeChange: (timeframe: PortfolioTimeframe) => void;
  /** True while the active timeframe's series is loading. */
  isLoading?: boolean;
  className?: string;
}

/**
 * Portfolio performance chart panel — the user's realized P&L over time
 * ({@link PortfolioPnlChart}) inside a styled Card, with a 1W / 1M / All
 * pill toggle below. The series comes from the api; a one-day-old account
 * has too little history to plot, so a friendly empty state stands in
 * until there are at least two daily points.
 */
export function PortfolioChart({ points, timeframe, onTimeframeChange, isLoading, className }: PortfolioChartProps) {
  return (
    <Card variant="default" className={cn('flex flex-col gap-3 p-3', className)}>
      <div className="relative h-[180px] lg:h-[260px] bg-surface rounded-[10px] overflow-hidden">
        {points.length >= 2 ? (
          <PortfolioPnlChart points={points} />
        ) : isLoading ? (
          <Skeleton className="absolute inset-0 rounded-[10px]" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-sm text-muted">Your P&L history will chart here as you trade.</p>
          </div>
        )}
      </div>

      <div
        role="tablist"
        aria-label="Chart timeframe"
        className="flex items-center justify-center gap-1 self-center bg-border/40 rounded-pill p-1"
      >
        {TIMEFRAMES.map((tf) => {
          const isActive = tf === timeframe;
          return (
            <button
              key={tf}
              role="tab"
              type="button"
              aria-selected={isActive}
              onClick={() => onTimeframeChange(tf)}
              className={cn(
                'px-3 py-1 text-xs font-semibold rounded-pill transition-colors',
                isActive ? 'bg-surface text-primary shadow-card' : 'text-muted hover:text-primary',
              )}
            >
              {tf}
            </button>
          );
        })}
      </div>
    </Card>
  );
}
